import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { AnnouncementsService } from '../services/announcements.service';
import { Announcement, AnnouncementPriority } from '../models/announcement.models';
import { LoggerService } from '../../../shared/services/logger.service';
import { AnnouncementHubService } from '../services/announcement-hub.service';
import { AnnouncementNotificationService } from '../services/announcement-notification.service';
import { PushNotificationService } from '../services/push-notification.service';
import {
  orderWidgetAnnouncements,
  isAckPendingForCaller as isAckPendingForCallerUtil,
  getAnnouncementDisplayDate,
  hasEditedMarker as hasEditedMarkerUtil,
  intlLocaleFor
} from './announcements-widget.utils';

@Component({
    selector: 'ri-announcements-widget',
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatBadgeModule,
        MatListModule,
        MatDividerModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './announcements-widget.component.html',
    styleUrls: ['./announcements-widget.component.scss'],
})
export class AnnouncementsWidgetComponent implements OnInit, OnDestroy {
  announcements: Announcement[] = [];
  /** Owned app-wide by AnnouncementNotificationService — same badge as the toolbar bell. */
  readonly attentionCount$ = this.notifications.attentionCount$;
  isLoading = true;
  loadError = false;
  isExpanded = false;
  maxDisplayed = 5;
  isSubscribingToPush = false;
  readonly AnnouncementPriority = AnnouncementPriority;

  private destroy$ = new Subject<void>();

  constructor(
    private AnnouncementsService: AnnouncementsService,
    private announcementHubService: AnnouncementHubService,
    private notifications: AnnouncementNotificationService,
    private router: Router,
    private logger: LoggerService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    readonly pushService: PushNotificationService
  ) { }

  ngOnInit(): void {
    this.loadAnnouncements();

    // The attention summary itself is refreshed by AnnouncementNotificationService on
    // every hub arrival already — this subscription only needs to reload the widget's
    // own recent-items list. The hub connection itself belongs to that service for the
    // whole session: this widget must never start or stop it, or leaving Home would
    // silence the bell, the banner and the blocking dialog until a reload.
    this.announcementHubService.announcementReceived$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadAnnouncements());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAnnouncements(): void {
    this.isLoading = true;
    this.loadError = false;
    this.cdr.markForCheck();
    this.AnnouncementsService.getMyAnnouncements()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (announcements) => {
          // Reorder before capping to maxDisplayed — a High item buried past position 5
          // by date alone would otherwise never surface (step 3 of the list brief).
          this.announcements = orderWidgetAnnouncements(announcements, this.maxDisplayed);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.logger.errorWithContext('AnnouncementsWidgetComponent', 'Error loading ANNOUNCEMENTS', error);
          this.isLoading = false;
          this.loadError = true;
          this.cdr.markForCheck();
        }
      });
  }

  onNotificationClick(notification: Announcement): void {
    if (notification.seen === false) {
      this.AnnouncementsService.markAsSeen(notification.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            notification.seen = true;
            // A full refresh rather than a local decrement: attention now also
            // depends on acknowledgment state, which a bare "seen" flip cannot infer.
            this.notifications.refresh();
            this.cdr.markForCheck();
          }
        });
    }
    this.router.navigate(['/announcements', notification.id]);
  }

  /** True only when the current caller is themself a recipient still owing an acknowledgment. */
  isAckPendingForCaller(notification: Announcement): boolean {
    return isAckPendingForCallerUtil(notification);
  }

  hasEditedMarker(notification: Announcement): boolean {
    return hasEditedMarkerUtil(notification);
  }

  /** publishedAt ?? createdAt — a scheduled item's createdAt predates its actual arrival (R7). */
  displayDate(notification: Announcement): string {
    return getAnnouncementDisplayDate(notification);
  }

  onViewAll(): void {
    this.router.navigate(['/announcements/my']);
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

  async onSubscribeToPush(): Promise<void> {
    if (this.isSubscribingToPush) return;
    this.isSubscribingToPush = true;
    this.cdr.markForCheck();
    try {
      await this.pushService.requestSubscription();
    } catch {
      // error already logged by the service
    } finally {
      this.isSubscribingToPush = false;
      this.cdr.markForCheck();
    }
  }

  getNotificationPreview(body: string): string {
    const doc = new DOMParser().parseFromString(body, 'text/html');
    const text = (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (text.length <= 100) return text;
    const cut = text.lastIndexOf(' ', 100);
    return text.substring(0, cut > 0 ? cut : 100) + '…';
  }

  getTimeAgo(date: string | Date): string {
    const now = new Date();
    const notificationDate = new Date(date);
    // Clock skew between server and device can put a just-sent item slightly in the future.
    const diffMs = Math.max(0, now.getTime() - notificationDate.getTime());
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return this.translate.instant('ANNOUNCEMENTS.WIDGET.TIME_MINUTES', { n: diffMins });
    } else if (diffHours < 24) {
      return this.translate.instant('ANNOUNCEMENTS.WIDGET.TIME_HOURS', { n: diffHours });
    } else if (diffDays < 7) {
      return this.translate.instant('ANNOUNCEMENTS.WIDGET.TIME_DAYS', { n: diffDays });
    } else {
      return notificationDate.toLocaleDateString(intlLocaleFor(this.translate.getCurrentLang()));
    }
  }
}


